import { Button } from "@arcadeai/design-system/components/ui/atoms/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@arcadeai/design-system/components/ui/atoms/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@arcadeai/design-system/components/ui/atoms/select";
import { cn } from "@arcadeai/design-system/lib/utils";
import { AlertTriangle, Loader2 } from "lucide-react";
import { useState } from "react";
import { getRoleStyle } from "@/app/roles/lib/role-style";

export type RoleSwitcherOption = { key: string; label: string };

type RoleSwitcherProps = {
  /** Selectable role options; each is rendered with its role-style icon. */
  options: RoleSwitcherOption[];
  /** Key of the member's current role. */
  currentKey: string;
  /** The member whose role is being changed, for the confirmation copy. */
  subject: { isCurrentUser: boolean; email: string };
  /**
   * Apply the chosen role. Return a promise (e.g. `mutateAsync`) so the dialog
   * closes on success and stays open on failure — the caller owns error toasts.
   */
  onConfirm: (roleKey: string) => Promise<unknown>;
  isPending: boolean;
  /** Confirmation dialog title, e.g. "Change role" or "Change organization role". */
  dialogTitle: string;
  /**
   * When the current user selects this key it's a self-demotion: the confirm turns
   * destructive and shows `selfDemotionMessage`. Omit if the surface has no such case.
   */
  selfDemotionKey?: string;
  selfDemotionMessage?: string;
  /** Width/layout classes for the trigger (defaults to a fixed width). */
  triggerClassName?: string;
};

/**
 * Presentational role picker: a Select plus a confirmation dialog. Scope-agnostic
 * — project and org member switchers wrap it, supplying their own options, current
 * role, mutation, and copy. Owns only the pending-selection state machine.
 */
export function RoleSwitcher({
  options,
  currentKey,
  subject,
  onConfirm,
  isPending,
  dialogTitle,
  selfDemotionKey,
  selfDemotionMessage,
  triggerClassName,
}: RoleSwitcherProps) {
  const [pendingKey, setPendingKey] = useState<string | null>(null);

  const labelFor = (key: string) =>
    options.find((option) => option.key === key)?.label;
  const currentLabel = labelFor(currentKey);
  const pendingLabel = pendingKey ? labelFor(pendingKey) : undefined;
  const isSelfDemotion =
    subject.isCurrentUser && pendingKey === selfDemotionKey;

  const handleConfirm = async () => {
    if (!pendingKey) {
      return;
    }
    try {
      await onConfirm(pendingKey);
      setPendingKey(null);
    } catch {
      // Leave the dialog open on failure; the mutation surfaces its own error.
    }
  };

  return (
    <>
      <Select
        disabled={isPending}
        onValueChange={(value) => {
          if (value && value !== currentKey) {
            setPendingKey(value);
          }
        }}
        value={currentKey}
      >
        <SelectTrigger
          className={cn("h-8 w-[170px]", triggerClassName)}
          size="sm"
        >
          <SelectValue>
            {(value) => labelFor(value) ?? currentLabel}
          </SelectValue>
        </SelectTrigger>
        <SelectContent>
          {options.map((option) => {
            const { icon: Icon } = getRoleStyle(option.key);
            return (
              <SelectItem key={option.key} value={option.key}>
                <span className="flex items-center gap-2">
                  <Icon className="size-3.5 text-muted-foreground" />
                  {option.label}
                </span>
              </SelectItem>
            );
          })}
        </SelectContent>
      </Select>

      <Dialog
        onOpenChange={(open) => !open && setPendingKey(null)}
        open={pendingKey !== null}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{dialogTitle}</DialogTitle>
            <DialogDescription>
              Change {subject.isCurrentUser ? "your" : `${subject.email}'s`}{" "}
              role from{" "}
              <span className="font-medium text-foreground">
                {currentLabel}
              </span>{" "}
              to{" "}
              <span className="font-medium text-foreground">
                {pendingLabel}
              </span>
              ?
            </DialogDescription>
          </DialogHeader>

          {isSelfDemotion && selfDemotionMessage ? (
            <div className="flex gap-2.5 rounded-md border border-warning/40 bg-warning/10 px-3 py-2.5">
              <AlertTriangle className="mt-0.5 size-3.5 shrink-0 text-warning" />
              <p className="text-foreground/80 text-xs leading-relaxed">
                {selfDemotionMessage}
              </p>
            </div>
          ) : null}

          <DialogFooter>
            <DialogClose
              render={
                <Button
                  disabled={isPending}
                  size="sm"
                  type="button"
                  variant="outline"
                />
              }
            >
              Cancel
            </DialogClose>
            <Button
              disabled={isPending}
              onClick={handleConfirm}
              size="sm"
              variant={isSelfDemotion ? "destructive" : "default"}
            >
              {isPending && <Loader2 className="size-4 animate-spin" />}
              Change role
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
